import React from 'react';
import { 
  Drawer, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  Box,
  Button,
  Chip,
  Avatar
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  TrackChanges as GoalsIcon,
  Assignment as TasksIcon,
  Event as WorkshopIcon,
  AccountTree as MappingIcon,
  GridOn as MatrixIcon,
  Logout as LogoutIcon,
  Business as TeamIcon,
  Build as SkillsIcon
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const drawerWidth = 280;

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
  { text: 'Team Members', icon: <PeopleIcon />, path: '/team' },
  { text: 'Goals', icon: <GoalsIcon />, path: '/goals' },
  { text: 'Tasks', icon: <TasksIcon />, path: '/tasks' },
  { text: 'Workshops', icon: <WorkshopIcon />, path: '/workshops' },
];

const visualizationItems = [
  { text: 'Goal Mapping', icon: <MappingIcon />, path: '/goal-mapping' },
  { text: 'Eisenhower Matrix', icon: <MatrixIcon />, path: '/eisenhower-matrix' },
  { text: 'Skills Mentoring', icon: <SkillsIcon />, path: '/skills-mentoring' },
];

const Navigation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, currentTeam, logout } = useAuth();

  const isAdmin = currentTeam?.role === 'admin' || currentTeam?.role === 'manager';

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const handleLogout = () => {
    logout();
  };

  const getInitials = (name: string) => {
    return name
      .split('.')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
        },
      }}
    >
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <MappingIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 600 }}>
            Strategy Mapper
          </Typography>
        </Box>
      </Toolbar>
      
      <Divider />
      
      {/* User and Team Info Section */}
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar 
            sx={{ 
              width: 40, 
              height: 40, 
              mr: 2, 
              bgcolor: 'primary.main',
              fontSize: '0.875rem'
            }}
          >
            {user ? getInitials(user.displayName) : '?'}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight="medium" noWrap>
              {user?.displayName || 'Unknown User'}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {user?.email || 'No email'}
            </Typography>
          </Box>
        </Box>

        {currentTeam && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <TeamIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                Current Team
              </Typography>
            </Box>
            <Typography variant="body2" fontWeight="medium" noWrap>
              {currentTeam.name}
            </Typography>
            <Chip 
              label={currentTeam.role} 
              size="small" 
              color="primary" 
              sx={{ mt: 0.5 }}
            />
          </Box>
        )}

        <Button
          size="small" 
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          fullWidth
          variant="outlined"
        >
          Logout
        </Button>
      </Box>
    
    <Divider />
    
    <List>
      {menuItems.map((item) => (
        <ListItem key={item.text} disablePadding>
          <ListItemButton
            selected={location.pathname === item.path}
            onClick={() => handleNavigate(item.path)}
            sx={{
              '&.Mui-selected': {
                backgroundColor: 'primary.light',
                color: 'primary.contrastText',
                '& .MuiListItemIcon-root': {
                  color: 'primary.contrastText',
                },
              },
            }}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItemButton>
        </ListItem>
      ))}
    </List>
    
    <Divider />
    
    {/* Only show visualizations to admins and managers */}
    {isAdmin && (
      <>
        <List>
          <ListItem>
            <ListItemText 
              primary="Workshop Tools" 
              primaryTypographyProps={{ 
                variant: 'subtitle2', 
                color: 'text.secondary',
                sx: { mt: 1, mb: 0.5 }
              }} 
            />
          </ListItem>
          {visualizationItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => handleNavigate(item.path)}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: 'secondary.light',
                    color: 'secondary.contrastText',
                    '& .MuiListItemIcon-root': {
                      color: 'secondary.contrastText',
                    },
                  },
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText 
                  primary={item.text}
                  secondary="Workshop Only"
                  secondaryTypographyProps={{
                    variant: 'caption',
                    color: 'text.secondary'
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </>
    )}
    </Drawer>
  );
};

export default Navigation;